import { uniqBy } from 'es-toolkit';
import React, { useState } from 'react';
import { Hud } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';

import {
  Component,
  Member,
  ParallelLine,
  PerpendicularLine,
  RelativeMemberGeometry,
  Splice,
} from '@paragon/contract-library';
import {
  Direction2D,
  distanceGreaterThanOrEqual,
  distanceLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Point2D,
  Vector2D,
} from '@paragon/geometry-library';
import { calculateLine, findLine } from '@paragon/relative-geometry';
import {
  calculateMemberMidline,
  calculateMemberOverallLengthSegment,
  calculateMemberPolygon,
} from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { nudgeReferenceLines } from '../../store/actions/editorActions';
import { leftClickItem } from '../../store/actions/sceneInteractionActions';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { EditableDistanceDimension } from '../dimension/EditableDistanceDimension';
import { getOffsetDistanceDimensionDefinition } from '../dimension/getDistanceDimensionDefinitionsForMember';
import { QuickDistanceDimension } from '../dimension/QuickDistanceDimension';

interface Props {
  readonly splice: Splice;
  readonly component: Component;
  readonly componentDesignGuid: string;
}

export function SpliceVisual({ splice, component, componentDesignGuid }: Props) {
  const dispatch = useAppDispatch();
  const selectedItem = useAppSelector(getSingleSelectedItemEntryOrNull);

  const [isHovered, setIsHovered] = useState(false);

  // splice visual interferes with the live plating feature
  if (selectedItem?.type === 'platePair') return;

  const member1 = component.members.find(({ guid }) => guid === splice.neighbor1Guid);
  const member2 = component.members.find(({ guid }) => guid === splice.neighbor2Guid);
  if (!member1 || !member2) return null;

  const spliceSegment = calculateSpliceSegment(member1, member2, component);
  if (!spliceSegment) return null;

  const isSelected = selectedItem?.type === 'splice' && selectedItem.item.guid === splice.guid;

  const spliceZDimension = Math.min(member1.thickness, member2.thickness);

  return (
    <>
      <BasicSpliceVisual
        spliceSegment={spliceSegment}
        spliceThickness={spliceZDimension}
        highlighted={isHovered || isSelected}
        onClick={(event) => dispatch(leftClickItem(event, { item: splice, type: 'splice' }))}
        onChangeHover={setIsHovered}
      />
      {isSelected && (
        <SpliceDimensionsVisual
          member1={member1}
          member2={member2}
          component={component}
          componentDesignGuid={componentDesignGuid}
          spliceSegment={spliceSegment}
        />
      )}
    </>
  );
}

export function BasicSpliceVisual(props: {
  spliceSegment: LineSegment2D;
  spliceThickness: number;
  highlighted: boolean;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
  onChangeHover?: (hovering: boolean) => void;
}) {
  const { spliceSegment, spliceThickness, highlighted, onClick, onChangeHover } = props;

  return (
    <Hud renderPriority={2}>
      <mesh
        position={spliceSegment.midpoint.to3D(spliceThickness / 2).toArray()}
        rotation={[0, 0, spliceSegment.direction.angleInRadians]}
        onClick={onClick}
        onPointerOver={onChangeHover && (() => onChangeHover(true))}
        onPointerOut={onChangeHover && (() => onChangeHover(false))}
      >
        <boxGeometry args={[spliceSegment.length, 0.5, spliceThickness]} />
        <meshBasicMaterial color={highlighted ? 'red' : 'black'} />
      </mesh>
    </Hud>
  );
}

/**
 * Calculates the splice segment between two members, or returns `null` if no splice exists. This is done client-side,
 * since the splices on the truss model might not be fully up-to-date if there have been changes to relative geometry
 * that haven't made it back from the server yet.
 */
export function calculateSpliceSegment(member1: Member, member2: Member, component: Component): LineSegment2D | null {
  const member1MidlineObject = calculateMemberMidline(member1, component);
  const member2MidlineObject = calculateMemberMidline(member2, component);
  if (!member1MidlineObject || !member2MidlineObject) return null;

  const member1Midline = LineSegment2D.fromSegmentObject(member1MidlineObject);
  const member2Midline = LineSegment2D.fromSegmentObject(member2MidlineObject);

  const member1Geometry = calculateMemberPolygon(member1, component);
  const member2Geometry = calculateMemberPolygon(member2, component);
  if (!member1Geometry || !member2Geometry) return null;

  const spliceSegment = member1Geometry.overlappingSegment(member2Geometry);
  if (
    !spliceSegment ||
    !spliceSegment.isPerpendicularTo(member1Midline) ||
    !spliceSegment.isPerpendicularTo(member2Midline)
  ) {
    return null;
  }

  return spliceSegment;
}

interface EditableReferenceLine {
  readonly referenceLine: ParallelLine | PerpendicularLine;
  readonly line: Line2D;
}

/**
 * Gets the reference line(s) that are used to define the splice. Will return `null` if any of the reference lines used
 * to define the splice are not editable by just changing their `offset` property.
 */
export function getEditableSpliceReferenceLines(
  member1: Member | null,
  member2: Member | null,
  component: Component,
  spliceSegment: LineSegment2D,
): EditableReferenceLine[] | null {
  if (!member1 || !member2 || !member1.relativeGeometry || !member2.relativeGeometry) return null;

  const member1ReferenceLines = getEditableSpliceReferenceLinesForMember(
    member1.relativeGeometry,
    component,
    spliceSegment,
  );
  const member2ReferenceLines = getEditableSpliceReferenceLinesForMember(
    member2.relativeGeometry,
    component,
    spliceSegment,
  );

  // If we can't find the splice reference lines for one of the members, then we presumably can't adjust the splice
  // because it would only be modifying the cut of one of the members.
  if (member1ReferenceLines.length === 0 || member2ReferenceLines.length === 0) return null;

  return uniqBy(member1ReferenceLines.concat(member2ReferenceLines), ({ referenceLine }) => referenceLine.guid);
}

function getEditableSpliceReferenceLinesForMember(
  relativeGeometry: RelativeMemberGeometry,
  component: Component,
  spliceSegment: LineSegment2D,
): EditableReferenceLine[] {
  return (
    relativeGeometry.baseCutGuids
      .concat(relativeGeometry.endCutGuids)
      .map((guid) => findLine(guid, component))
      .filter(notNullish)
      // We only support editing parallel and perpendicular reference lines since they have an easy-to-edit offset property
      .filter((referenceLine) => referenceLine.lineType === 'Parallel' || referenceLine.lineType === 'Perpendicular')
      .map((referenceLine) => {
        const line = calculateLine(referenceLine, component);
        if (!line) return null;
        return { referenceLine, line };
      })
      .filter(notNullish)
      .filter(({ line }) => line.containsSegment(spliceSegment))
  );
}

interface SpliceDimensionsVisualProps {
  readonly member1: Member;
  readonly member2?: Member;
  readonly component: Component;
  readonly componentDesignGuid: string;
  readonly spliceSegment: LineSegment2D;
}

export function SpliceDimensionsVisual({
  member1,
  member2,
  component,
  componentDesignGuid,
  spliceSegment,
}: SpliceDimensionsVisualProps) {
  const dispatch = useAppDispatch();

  const editableSpliceReferenceLines =
    member2 && getEditableSpliceReferenceLines(member1, member2, component, spliceSegment);

  const member1OverallLengthSegmentObject = calculateMemberOverallLengthSegment(member1, component);
  const member2OverallLengthSegmentObject = member2 && calculateMemberOverallLengthSegment(member2, component);
  if (!member1OverallLengthSegmentObject || (member2 && !member2OverallLengthSegmentObject)) return null;

  const member1OverallLengthSegment = LineSegment2D.fromSegmentObject(member1OverallLengthSegmentObject);
  const member2OverallLengthSegment =
    member2OverallLengthSegmentObject && LineSegment2D.fromSegmentObject(member2OverallLengthSegmentObject);

  const member13DSegment = {
    start: member1OverallLengthSegment.basePoint.to3D().toObject(),
    end: member1OverallLengthSegment.endPoint.to3D().toObject(),
  };
  const member1DimensionDefinition = getOffsetDistanceDimensionDefinition(member13DSegment, 1);

  const member23DSegment = member2OverallLengthSegment && {
    start: member2OverallLengthSegment.basePoint.to3D().toObject(),
    end: member2OverallLengthSegment.endPoint.to3D().toObject(),
  };
  const member2DimensionDefinition = member23DSegment && getOffsetDistanceDimensionDefinition(member23DSegment, 1);

  const onChangeDimensionValue = (newValue: number, memberOverallLengthSegment: LineSegment2D) => {
    if (!editableSpliceReferenceLines || !member2OverallLengthSegment) return;
    if (
      distanceLessThanOrEqual(newValue, 0) ||
      distanceGreaterThanOrEqual(newValue, member1OverallLengthSegment.length + member2OverallLengthSegment.length)
    ) {
      return;
    }

    const offsetVector = getOffsetVector(spliceSegment.midpoint, memberOverallLengthSegment, newValue);
    const referenceLineNudges = editableSpliceReferenceLines.map(({ line, referenceLine }) => ({
      referenceLineGuid: referenceLine.guid,
      nudge: line.direction.rotateQuarterTurn().isOrientedWith(offsetVector.direction)
        ? offsetVector.magnitude
        : -offsetVector.magnitude,
    }));
    dispatch(nudgeReferenceLines(referenceLineNudges, componentDesignGuid));
  };

  const member1DimensionGeometry = LineSegment2D.fromSegmentObject(member1DimensionDefinition);
  let firstDimensionSegment, secondDimensionSegment;
  if (!member2DimensionDefinition) {
    const projection = spliceSegment.midpoint.projectOntoLineSegment(member1DimensionGeometry);
    if (!projection || !member1DimensionGeometry.containsOnInside(projection)) return null;
    firstDimensionSegment = new LineSegment2D(member1DimensionGeometry.basePoint, projection);
    secondDimensionSegment = new LineSegment2D(projection, member1DimensionGeometry.endPoint);
  } else {
    firstDimensionSegment = member1DimensionGeometry;
    secondDimensionSegment = LineSegment2D.fromSegmentObject(member2DimensionDefinition);
  }

  return editableSpliceReferenceLines && member2OverallLengthSegment ? (
    <>
      <EditableDistanceDimension
        start={firstDimensionSegment.basePoint}
        end={firstDimensionSegment.endPoint}
        distance={firstDimensionSegment.length}
        onChange={(newValue) => onChangeDimensionValue(newValue, member1OverallLengthSegment)}
        textFloat={4}
      />
      <EditableDistanceDimension
        start={secondDimensionSegment.basePoint}
        end={secondDimensionSegment.endPoint}
        distance={secondDimensionSegment.length}
        onChange={(newValue) => onChangeDimensionValue(newValue, member2OverallLengthSegment)}
        textFloat={4}
      />
    </>
  ) : (
    <>
      <QuickDistanceDimension
        start={firstDimensionSegment.basePoint}
        end={firstDimensionSegment.endPoint}
        distance={firstDimensionSegment.length}
        textFloat={4}
      />
      <QuickDistanceDimension
        start={secondDimensionSegment.basePoint}
        end={secondDimensionSegment.endPoint}
        distance={secondDimensionSegment.length}
        textFloat={4}
      />
    </>
  );
}

function getOffsetVector(
  spliceLocation: Point2D,
  memberOverallLengthSegment: LineSegment2D,
  newMemberLength: number,
): Vector2D {
  const [spliceEnd, nonSpliceEnd] =
    memberOverallLengthSegment.basePoint.distanceTo(spliceLocation) <=
    memberOverallLengthSegment.endPoint.distanceTo(spliceLocation)
      ? [memberOverallLengthSegment.basePoint, memberOverallLengthSegment.endPoint]
      : [memberOverallLengthSegment.endPoint, memberOverallLengthSegment.basePoint];
  const deltaChangeToMemberLength = newMemberLength - memberOverallLengthSegment.length;
  const directionToMoveSplice = Direction2D.fromPoints(nonSpliceEnd, spliceEnd);
  return Vector2D.fromDirectionAndMagnitude(directionToMoveSplice, deltaChangeToMemberLength);
}
